"use client";

import { LearningRichContent } from "@/components/student/LearningRichContent";
import {
  LessonSectionFrame,
  lessonSectionBodyGapClass,
  lessonSectionBodyProseClass,
  stripSectionNumbering,
} from "@/components/student/LessonSectionFrame";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import { PagedContentSections, type ContentPage } from "@/components/student/PagedContentSections";

export type ProfessionalProactiveSection = {
  section_number: number;
  heading: string;
  content: string;
};

export type ProfessionalProactiveStructuredData = {
  title: string;
  lesson_title: string;
  lesson_type: string;
  personalization_summary: {
    domain?: string;
    career_goal?: string;
    tier?: string;
    department?: string;
    learning_objectives: string[];
  };
  sections: ProfessionalProactiveSection[];
  workplace_scenario: {
    heading: string;
    scenario: string;
  };
  reflection_prompt: string;
};

function isValidProfessionalProactiveSection(s: unknown): s is ProfessionalProactiveSection {
  if (typeof s !== "object" || s === null) return false;
  const sec = s as Record<string, unknown>;
  return (
    typeof sec.section_number === "number" &&
    typeof sec.heading === "string" &&
    typeof sec.content === "string"
  );
}

export function isProfessionalProactiveStructuredData(
  v: unknown,
): v is ProfessionalProactiveStructuredData {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  if (typeof o.title !== "string") return false;
  if (typeof o.lesson_title !== "string") return false;
  if (typeof o.lesson_type !== "string") return false;
  if (!Array.isArray(o.sections) || !o.sections.every(isValidProfessionalProactiveSection)) return false;
  if (typeof o.personalization_summary !== "object" || o.personalization_summary === null)
    return false;
  const p = o.personalization_summary as Record<string, unknown>;
  if (!Array.isArray(p.learning_objectives)) return false;
  if (!o.workplace_scenario && !o.domain_use_case) return false;
  return true;
}

interface ProfessionalProactiveLessonContentProps {
  data: ProfessionalProactiveStructuredData;
  lessonCompletedInCourse?: boolean;
  /** True while the personalized workplace scenario is still generating in the background. */
  workplaceScenarioPending?: boolean;
}

export default function ProfessionalProactiveLessonContent({
  data,
  lessonCompletedInCourse = false,
  workplaceScenarioPending = false,
}: ProfessionalProactiveLessonContentProps) {
  const sections = [...data.sections].sort((a, b) => a.section_number - b.section_number);
  const { learning_objectives } = data.personalization_summary;
  const ws = (data as unknown as Record<string, unknown>).workplace_scenario as unknown;
  const duc = (data as unknown as Record<string, unknown>).domain_use_case as unknown;
  const wsObj = ws && typeof ws === "object" ? (ws as Record<string, unknown>) : null;
  const ducObj = duc && typeof duc === "object" ? (duc as Record<string, unknown>) : null;
  const workplaceHeading =
    (typeof wsObj?.heading === "string" && normalizeGeneratedText(wsObj.heading)) ||
    (typeof wsObj?.title === "string" && normalizeGeneratedText(wsObj.title)) ||
    (typeof ducObj?.heading === "string" && normalizeGeneratedText(ducObj.heading)) ||
    "Workplace scenario";

  const workplaceScenario =
    (typeof wsObj?.scenario === "string" && normalizeGeneratedText(wsObj.scenario)) ||
    (typeof wsObj?.content === "string" && normalizeGeneratedText(wsObj.content)) ||
    (typeof wsObj?.markdown === "string" && normalizeGeneratedText(wsObj.markdown)) ||
    (typeof ws === "string" && normalizeGeneratedText(ws)) ||
    (typeof ducObj?.scenario === "string" && normalizeGeneratedText(ducObj.scenario)) ||
    "";
  const reflectionPrompt = typeof data.reflection_prompt === "string" ? normalizeGeneratedText(data.reflection_prompt) : "";

  // Content sections, plus a trailing "Workplace Scenario" part (scenario + reflection),
  // mirroring the old system's final "Workplace Scenarios" tab.
  const pages: ContentPage[] = [
    ...sections.map((section, index) => ({
      id: section.section_number,
      label: stripSectionNumbering(section.heading) || `Part ${index + 1}`,
      node: (
        <LessonSectionFrame
          sectionNumber={section.section_number}
          heading={stripSectionNumbering(section.heading)}
        >
          <LearningRichContent
            content={section.content || ""}
            className={lessonSectionBodyProseClass}
            segmentGapClassName={lessonSectionBodyGapClass}
          />
        </LessonSectionFrame>
      ),
    })),
    {
      id: "workplace-scenario",
      label: "Workplace Scenario",
      node: (
        <>
          <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                Workplace scenario
              </span>
            </div>
            <h2 className="text-base font-semibold text-slate-900 mt-0">{workplaceHeading}</h2>
            {workplaceScenarioPending ? (
              <div className="flex items-center gap-2 text-sm text-emerald-900/80 italic">
                <span
                  className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-emerald-300 border-t-emerald-600"
                  aria-hidden
                />
                <span>Personalizing this scenario for your domain and industry focus…</span>
              </div>
            ) : workplaceScenario ? (
              <p className="text-slate-700 whitespace-pre-line mb-0">{workplaceScenario}</p>
            ) : (
              <p className="text-sm text-emerald-900/80 italic mb-0">
                Personalized workplace scenario will appear here based on your domain and industry focus.
              </p>
            )}
          </section>
          {reflectionPrompt && (
            <section className="mt-6 rounded-xl border border-violet-200 bg-violet-50/50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center rounded-full bg-violet-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                  Reflect
                </span>
              </div>
              <p className="text-slate-700 italic mb-0">{reflectionPrompt}</p>
            </section>
          )}
        </>
      ),
    },
  ];

  return (
    <article className="learning-content max-w-none prose prose-gray max-w-none">

      {/* Learning objectives */}
      <section className="mb-8 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center rounded-full bg-indigo-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
            Learning objectives
          </span>
        </div>
        <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
          {learning_objectives.map((o, i) => (
            <li key={i}>{normalizeGeneratedText(o)}</li>
          ))}
        </ul>
      </section>

      <PagedContentSections pages={pages} lessonCompletedInCourse={lessonCompletedInCourse} />
    </article>
  );
}
